import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import Seo from "@/components/Seo";

const TYPES = [
  { key: "general", label: "General question" },
  { key: "partnership", label: "Partnership / collaboration" },
  { key: "support", label: "Account or technical help" },
  { key: "media", label: "Press / media" },
  { key: "ministry", label: "Ministry inquiry" },
];

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [inquiry, setInquiry] = useState("general");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { name, email, subject, message, inquiry_type: inquiry });
      setSent(true);
      toast.success("Message received");
    } catch {
      toast.error("Could not send — please try again");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <div className="jj-card p-10 fade-up">
          <Mail className="h-10 w-10 text-burgundy mx-auto" strokeWidth={1.4} />
          <h1 className="font-serif text-4xl text-jjblue mt-5">Thank you</h1>
          <p className="mt-3 text-[var(--jj-text-secondary)]">
            Your message has been received. We'll respond as soon as we're able — usually within 1–2 days.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <Seo
        title="Contact · Jesus Just Us"
        description="Questions, partnerships, prayer needs, ideas — share anything on your heart with the Jesus Just Us team. Email connect@jesusjustus.us."
        path="/contact"
      />
      <div className="overline text-burgundy">Contact</div>
      <h1 className="font-serif text-5xl text-jjblue mt-3">We'd love to hear from you</h1>
      <p className="mt-4 text-[var(--jj-text-secondary)]">
        Questions, partnerships, prayer needs, ideas — share anything on your heart.
      </p>
      <p className="mt-3 text-sm text-[var(--jj-text-secondary)]">
        Or email us directly at{" "}
        <a href="mailto:connect@jesusjustus.us" className="text-burgundy font-medium hover:underline" data-testid="contact-mailto">
          connect@jesusjustus.us
        </a>
      </p>

      <form onSubmit={submit} className="jj-card p-7 mt-10 space-y-4" data-testid="contact-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-jjblue/80">Name</label>
            <input required className="input-jj mt-1" value={name} onChange={(e) => setName(e.target.value)} data-testid="contact-name" />
          </div>
          <div>
            <label className="text-sm text-jjblue/80">Email</label>
            <input required type="email" className="input-jj mt-1" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="contact-email" />
          </div>
        </div>
        <div>
          <label className="text-sm text-jjblue/80">Inquiry type</label>
          <select className="input-jj mt-1" value={inquiry} onChange={(e) => setInquiry(e.target.value)} data-testid="contact-type">
            {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm text-jjblue/80">Subject</label>
          <input required className="input-jj mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="contact-subject" />
        </div>
        <div>
          <label className="text-sm text-jjblue/80">Message</label>
          <textarea required rows={6} className="input-jj mt-1" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="contact-message" />
        </div>
        <div className="flex justify-end pt-2">
          <button disabled={busy} className="btn-primary flex items-center gap-2" data-testid="contact-submit">
            <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send message"}
          </button>
        </div>
      </form>
    </div>
  );
}
